import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Search, X, Menu, Plus } from 'react-feather';
import { useDebouncedCallback } from 'use-debounce';
import CO2Chart from "@/pages/CO2savings/co2.jsx";
import RecyclabilityScore from '@/pages/Recycle/RecyclabilityScore.jsx';
import { RecyclingChart } from '@/pages/Recycle/recycling-chart.jsx';
import TreesEquivalent from '../Recycle/TreesEquivalent';
import Costsavingbarchart from '../costsavings/Costsavingbars.jsx';
import ComplianceCostSavingsCard from '../costsavings/ComplianceSavingsCard';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Chart mapping
const chartComponents = {
  co2chart: <CO2Chart />,
  recyclibityscorechart: <RecyclabilityScore />,
  recyclingratechart: <RecyclingChart />,
  treesquivalent: <TreesEquivalent />,
  costsavingsbarchart: <Costsavingbarchart />,
  compliancecostsavings: <ComplianceCostSavingsCard />
};

// All chart metadata
const allCharts = [
  {
    id: 'co2chart',
    title: 'CO2 Emissions',
    keywords: ['carbon', 'savings', 'environment', 'emissions']
  },
  {
    id: 'recyclibityscorechart',
    title: 'Recyclability Score',
    keywords: ['recycle', 'Recyclability', 'Recyclability Score']
  },
  {
    id: 'recyclingratechart',
    title: 'Recycling Rate',
    keywords: ['recycle', 'rate', 'percentage']
  },
  {
    id: 'treesquivalent',
    title: 'Trees Equivalent',
    keywords: ['trees', 'equivalent', 'environment', 'co2']
  },
  {
    id: 'costsavingsbarchart',
    title: 'Cost Savings Bar Chart',
    keywords: ['cost', 'savings', 'financial', 'budget', 'pmd','organic', 'paper', 'plastic', 'glass']
  },
  {
    id: 'compliancecostsavings',
    title: 'Compliance Cost Savings',
    keywords: ['compliance', 'cost', 'savings', 'financial', 'budget']
  }
];

const STORAGE_KEY = 'dashboard_charts';

// Helper function to ensure layout has all required properties
const ensureLayout = (layout) => ({
  i: layout.i,
  x: layout.x || 0,
  y: layout.y || 0,
  w: layout.w || 4,
  h: layout.h || 3,
  minW: 2,
  minH: 2
});

// Memoized Chart Component
const MemoizedChart = React.memo(({ id }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === containerRef.current) {
          const { width, height } = entry.contentRect;
          setDimensions({ 
            width: Math.max(width - 30, 100),
            height: Math.max(height - 60, 100)
          });
        }
      }
    });

    resizeObserverRef.current.observe(containerRef.current);

    return () => {
      if (resizeObserverRef.current && containerRef.current) {
        resizeObserverRef.current.unobserve(containerRef.current);
      }
      resizeObserverRef.current?.disconnect();
    };
  }, [id]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: '100px',
        position: 'relative'
      }}
    >
      {React.cloneElement(chartComponents[id], {
        width: dimensions.width,
        height: dimensions.height,
        key: `${id}-${dimensions.width}-${dimensions.height}`
      })}
    </div>
  );
});

const Dashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCharts, setActiveCharts] = useState([]);
  const [layouts, setLayouts] = useState({
    lg: [], md: [], sm: [], xs: [], xxs: []
  });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [filteredCharts, setFilteredCharts] = useState([]);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const searchInputRef = useRef(null);

  // Load saved data
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setActiveCharts(parsed.activeCharts || []);
        
        // Ensure all layouts have required properties
        const sanitizedLayouts = {};
        ['lg', 'md', 'sm', 'xs', 'xxs'].forEach(size => {
          sanitizedLayouts[size] = (parsed.layouts?.[size] || []).map(ensureLayout);
        });
        
        setLayouts(sanitizedLayouts);
      } catch (err) {
        console.error('Error parsing saved layout:', err);
      }
    }
  }, []);

  // Save data
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      activeCharts,
      layouts
    }));
  }, [activeCharts, layouts]);

  // Filter charts based on search
  useEffect(() => {
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      const matches = allCharts.filter(chart =>
        chart.title.toLowerCase().includes(lower) ||
        chart.keywords.some(k => k.toLowerCase().includes(lower))
      );
      setFilteredCharts(matches);
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  }, [searchTerm]);

  const toggleSearchBar = () => {
    setShowSearchBar(!showSearchBar);
    if (!showSearchBar) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    } else {
      setSearchTerm('');
      setShowSearchResults(false);
    }
  };

  const addChart = useCallback((chartId) => {
    if (!activeCharts.find(c => c.id === chartId)) {
      const chart = allCharts.find(c => c.id === chartId);
      const newChart = {
        ...chart,
        pinned: false
      };
      
      // Initialize layouts for all breakpoints with proper values
      const newLayouts = {
        lg: ensureLayout({ 
          i: chartId, 
          x: (activeCharts.length * 2) % 12, 
          y: Infinity, 
          w: 4, 
          h: 3 
        }),
        md: ensureLayout({ 
          i: chartId, 
          x: (activeCharts.length * 2) % 10, 
          y: Infinity, 
          w: 4, 
          h: 3 
        }),
        sm: ensureLayout({ 
          i: chartId, 
          x: (activeCharts.length * 2) % 6, 
          y: Infinity, 
          w: 3, 
          h: 3 
        }),
        xs: ensureLayout({ 
          i: chartId, 
          x: (activeCharts.length * 2) % 4, 
          y: Infinity, 
          w: 2, 
          h: 3 
        }),
        xxs: ensureLayout({ 
          i: chartId, 
          x: (activeCharts.length * 2) % 2, 
          y: Infinity, 
          w: 1, 
          h: 3 
        })
      };

      setActiveCharts(prev => [...prev, newChart]);
      setLayouts(prev => ({
        lg: [...(prev.lg || []), newLayouts.lg],
        md: [...(prev.md || []), newLayouts.md],
        sm: [...(prev.sm || []), newLayouts.sm],
        xs: [...(prev.xs || []), newLayouts.xs],
        xxs: [...(prev.xxs || []), newLayouts.xxs]
      }));
    }
    setSearchTerm('');
    setShowSearchResults(false);
    setShowSearchBar(false);
  }, [activeCharts]);

  const removeChart = useCallback((chartId) => {
    setActiveCharts(prev => prev.filter(c => c.id !== chartId));
    setLayouts(prev => ({
      lg: (prev.lg || []).filter(l => l.i !== chartId),
      md: (prev.md || []).filter(l => l.i !== chartId),
      sm: (prev.sm || []).filter(l => l.i !== chartId),
      xs: (prev.xs || []).filter(l => l.i !== chartId),
      xxs: (prev.xxs || []).filter(l => l.i !== chartId)
    }));
  }, []);

  const onLayoutChange = useDebouncedCallback((currentLayout, allLayouts) => {
    setLayouts(prev => {
      // Check if layouts have actually changed
      const hasChanged = Object.keys(allLayouts).some(size => {
        const newLayout = allLayouts[size];
        const oldLayout = prev[size] || [];
        
        if (newLayout.length !== oldLayout.length) return true;
        
        return newLayout.some((item, index) => {
          const oldItem = oldLayout[index];
          return !oldItem || 
            item.x !== oldItem.x || 
            item.y !== oldItem.y || 
            item.w !== oldItem.w || 
            item.h !== oldItem.h;
        });
      });

      if (!hasChanged) return prev;

      // Ensure all layouts have required properties
      const sanitizedLayouts = {};
      Object.keys(allLayouts).forEach(size => {
        sanitizedLayouts[size] = allLayouts[size].map(ensureLayout);
      });
      return sanitizedLayouts;
    });
  }, 100);

  return (
    <div className="react-grid-layout-demo">
      <div className="search-container">
        <div className={`search-form ${showSearchBar ? 'expanded' : ''}`}>
          {showSearchBar ? (
            <div className="search-input-container">
              <Search size={18} className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for charts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => searchTerm && setShowSearchResults(true)}
              />
              {searchTerm && (
                <button 
                  className="clear-search" 
                  onClick={() => {
                    setSearchTerm('');
                    setShowSearchResults(false);
                  }}
                >
                  <X size={18} />
                </button>
              )}
            </div>
          ) : (
            <button className="add-chart-button" onClick={toggleSearchBar}>
              <Plus size={20} />
              <span>Add Chart</span>
            </button>
          )}
        </div>
      </div>

      {showSearchResults && filteredCharts.length > 0 && (
        <div className="search-results-container">
          <div className="search-results">
            {filteredCharts.map(chart => (
              <div
                key={chart.id}
                className="search-result-item"
                onClick={() => addChart(chart.id)}
              >
                {chart.title}
                {activeCharts.some(c => c.id === chart.id) && (
                  <span className="already-added">(Added)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        onLayoutChange={onLayoutChange}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        rowHeight={100}
        draggableHandle=".drag-handle"
        isResizable={true}
        isDraggable={true}
      >
        {activeCharts.map((chart) => (
          <div 
            key={chart.id} 
            data-grid={{ 
              i: chart.id,
              ...(layouts.lg.find(l => l.i === chart.id) || { x: 0, y: 0, w: 4, h: 3 })
            }}
            className={`chart-item ${chart.pinned ? 'pinned' : ''}`}
          >
            <div className="chart-actions">
              <span className="drag-handle">
                <Menu size={16} />
              </span>
              <button 
                onClick={() => removeChart(chart.id)} 
                className="remove-button"
              >
                <X size={16} />
              </button>
            </div>
            <div className="chart-content">
              <MemoizedChart id={chart.id} />
            </div>
          </div>
        ))}
      </ResponsiveGridLayout>

      <style jsx>{`
        .react-grid-layout-demo {
          padding: 20px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        .search-container {
          margin-bottom: 20px;
          display: flex;
          justify-content: flex-end;
        }
        
        .search-form {
          transition: all 0.3s ease;
          width: auto;
        }
        
        .search-form.expanded {
          width: 100%;
          max-width: 400px;
        }
        
        .search-input-container {
          position: relative;
          width: 100%;
        }
        
        .search-input-container input {
          width: 100%;
          padding: 12px 40px 12px 40px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 16px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        
        .search-icon {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #888;
        }
        
        .clear-search {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #888;
          padding: 0;
        }
        
        .clear-search:hover {
          color: #555;
        }
        
        .add-chart-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background-color: #4a6bdf;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
        }
        
        .add-chart-button:hover {
          background-color: #3a56c4;
        }
        
        .search-results-container {
          margin-bottom: 20px;
          width: 100%;
        }
        
        .search-results {
          background: white;
          border: 1px solid #ddd;
          border-radius: 6px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          z-index: 100;
          max-height: 300px;
          overflow-y: auto;
        }
        
        .search-result-item {
          padding: 12px 20px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .search-result-item:hover {
          background-color: #f5f5f5;
        }
        
        .already-added {
          color: #888;
          font-size: 0.9em;
          margin-left: 10px;
        }
        
        .chart-item {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 15px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
          transition: transform 200ms ease;
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
        }
        
        .chart-item:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .chart-item.pinned {
          border-left: 4px solid #4a6bdf;
          background-color: #f8f9ff;
        }
        
        .chart-actions {
          display: flex;
          justify-content: flex-end;
          gap: 5px;
          margin-bottom: 10px;
        }
        
        .drag-handle {
          cursor: move;
          color: #666;
          opacity: 0.7;
          transition: all 0.2s;
          margin-right: auto;
        }
        
        .drag-handle:hover {
          opacity: 1;
          color: #333;
        }
        
        .remove-button {
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .remove-button:hover {
          background: #fee2e2;
          border-color: #fca5a5;
          color: #dc2626;
        }
        
        .chart-content {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
        }
        
        .react-grid-item {
          transition: transform 200ms ease;
        }
        
        .react-grid-item.cssTransforms {
          transition-property: transform;
        }
        
        .react-grid-item.resizing {
          z-index: 1;
          will-change: width, height;
          transition: none;
        }
        
        .react-grid-item.react-draggable-dragging {
          transition: none;
          z-index: 3;
          will-change: transform;
        }
        
        .react-grid-item.react-grid-placeholder {
          background: #e0e7ff;
          opacity: 0.6;
          transition-duration: 100ms;
          z-index: 2;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          -o-user-select: none;
          user-select: none;
        }
        
        .react-grid-item > .react-resizable-handle {
          position: absolute;
          width: 20px;
          height: 20px;
          bottom: 0;
          right: 0;
          background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+');
          background-position: bottom right;
          padding: 0 3px 3px 0;
          background-repeat: no-repeat;
          background-origin: content-box;
          box-sizing: border-box;
          cursor: se-resize;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;